import { ActionTree, MutationTree, GetterTree } from 'vuex'
import { Capacitor } from '@capacitor/core'
import { Preferences as Storage } from '@capacitor/preferences'
import {
  CONFIG_API,
  LOCATION_CURRENT_API,
  CATALOG_MENU_API,
} from '~/assets/config/api-path'
import { IStateIndex } from '~/store/index'
import { ICity } from '~/assets/interfaces/location'
import { IMobileAppList } from '~/assets/interfaces/mobile-app'
import { ISocialLinkList } from '~/assets/interfaces/socials'
import { ILinkItem } from '~/assets/interfaces/navigation'
import { IImageVariants } from '~/assets/interfaces/images'
import {
  IHeaderBanner,
  ISiteOption,
  ISiteOptionSession,
  ISiteScriptItem,
  ISiteUnderReconstruction,
} from '~/assets/interfaces/site'
import { ICssVariables } from '~/assets/interfaces/classes'
import { IContacts } from '~/assets/interfaces/contacts'
import {
  ILayoutFooterSitemapItem,
  ISettingsLayout,
} from '~/assets/interfaces/layout'
import { IPaySystem } from '~/assets/interfaces/paysystems'
import { ICategoryInMenu } from '~/assets/interfaces/categories'
import { runIdle } from '~/assets/functions/runIdle'

interface ISiteLogo {
  mobile: {
    image: IImageVariants | null
    aspectRatio: string
  }
  desktop: {
    image: IImageVariants | null
    aspectRatio: string
  }
}

type ApiKeys = Record<string, string>

const DEFAULT_SITE_OPTIONS: ISiteOption = {
  currency: '₽',
  currencyCode: 'RUB',
  phoneMask: '(+7|8) 999 999 99 99',
  copyright: true,
  canChangeQuantity: false,
  authType: 'sms',
  isOnlyUserToAccessOrder: false,
  catalogPageType: 'type-1',
  isRunSliderImmediately: false,
  productShadow: false,
  productInStockViewType: 'type-1',
}

const DEFAULT_OPTIONS_SESSION: ISiteOptionSession = {
  isShowComponentLocationQuestion: false,
}

interface ISiteConfigResponse {
  token: string
  isAuthorized?: boolean
  mobileApp?: IMobileAppList | null
  social?: ISocialLinkList | null
  socialHeader?: ISocialLinkList | null
  footerContacts?: ILinkItem[]
  companyName?: string
  copyright?: string
  logo?: ISiteLogo | null
  brand?: string
  origin?: string
  contacts?: IContacts | null
  paySystems?: IPaySystem[]
  optionsSession?: Partial<ISiteOptionSession> | null
  headerBanner?: IHeaderBanner | null
  isUnderConstruction?: boolean | ISiteUnderReconstruction
  headerMenu?: ILinkItem[]
  footerSitemap?: ILayoutFooterSitemapItem[]
  options?: Partial<ISiteOption> | null
  apiKeys?: ApiKeys | null
  layout?: ISettingsLayout
  cssVariables?: ICssVariables | null
  scripts?: ISiteScriptItem[] | null
  dadata?: { token: string } | null
}

export interface IState {
  catalogMenuCategories: ICategoryInMenu[]
  location: ICity | null
  ip: string
  mobileApp: IMobileAppList
  social: ISocialLinkList
  socialHeader: ISocialLinkList
  token: string | null
  footerContacts: ILinkItem[]
  companyName: string
  logo: ISiteLogo | null
  brand: string
  origin: string
  options: ISiteOption
  optionsSession: ISiteOptionSession
  cssVariables: ICssVariables | null
  scripts: ISiteScriptItem[]
  apiKeys: ApiKeys
  hiddenCopyright: boolean
  copyright: string
  isUnderConstruction: ISiteUnderReconstruction | boolean
  contacts: IContacts
  layout: ISettingsLayout
  headerBanner: IHeaderBanner | null
  fixedHeaderHeight: number | null
  paySystems: IPaySystem[]
  dadata?: { token: string }
  host: null | string
  isShowAppBanner: boolean
  isConfigLoad: boolean
  headerMenu: ILinkItem[]
  footerSitemap: ILayoutFooterSitemapItem[]
  cookieAccepted: boolean
}

export const state = (): IState => ({
  catalogMenuCategories: [],
  location: null,
  ip: '',
  mobileApp: {},
  social: {},
  socialHeader: {},
  token: null,
  footerContacts: [],
  companyName: '',
  logo: null,
  brand: '',
  copyright: '',
  origin: '',
  options: { ...DEFAULT_SITE_OPTIONS },
  optionsSession: { ...DEFAULT_OPTIONS_SESSION },
  cssVariables: null,
  scripts: [],
  apiKeys: {},
  hiddenCopyright: false,
  isUnderConstruction: false,
  contacts: {},
  layout: {},
  headerBanner: null,
  headerMenu: [],
  fixedHeaderHeight: null,
  paySystems: [],
  host: null,
  isShowAppBanner: true,
  isConfigLoad: false,
  footerSitemap: [],
  cookieAccepted: false,
})

export const mutations: MutationTree<IState> = {
  setLocation(state: IState, val: ICity | null) {
    state.location = val
  },
  setCatalogMenuCategories(
    state: IState,
    catalogMenuCategories: ICategoryInMenu[]
  ) {
    state.catalogMenuCategories = catalogMenuCategories
  },
  setMobileApp(state: IState, val?: IMobileAppList | null) {
    state.mobileApp = val || {}
  },
  setIP(state: IState, val: string) {
    state.ip = val
  },
  setToken(state: IState, val: string | null) {
    state.token = val
  },
  setSocial(state: IState, val?: ISocialLinkList | null) {
    state.social = val || {}
  },
  setSocialHeader(state: IState, val?: ISocialLinkList | null) {
    state.socialHeader = val || {}
  },
  setFooterContacts(state: IState, val: ILinkItem[]) {
    state.footerContacts = val
  },
  setContacts(state: IState, val?: IContacts | null) {
    state.contacts = val || {}
  },
  setCompanyName(state: IState, val: string) {
    state.companyName = val
  },
  setCopyright(state: IState, val: string) {
    state.copyright = val
  },
  setApiKeys(state: IState, val?: ApiKeys | null) {
    state.apiKeys = val || {}
  },
  setLogo(state: IState, val?: ISiteLogo | null) {
    state.logo = val || null
  },
  setBrand(state: IState, val: string) {
    state.brand = val
  },
  setOrigin(state: IState, val: string) {
    state.origin = val
  },
  setOptions(state: IState, val?: Partial<ISiteOption> | null) {
    state.options = {
      ...DEFAULT_SITE_OPTIONS,
      ...(val || {}),
    }
  },
  setOptionsSession(state: IState, val?: Partial<ISiteOptionSession> | null) {
    state.optionsSession = {
      ...DEFAULT_OPTIONS_SESSION,
      ...(val || {}),
    }
  },
  setCssVariables(state: IState, val: ICssVariables | null) {
    state.cssVariables = val
  },
  setScripts(state: IState, val?: ISiteScriptItem[] | null) {
    state.scripts = val || []
  },
  setHiddenCopyright(state: IState, val: boolean) {
    state.hiddenCopyright = val
  },
  setIsUnderConstruction(
    state: IState,
    val: boolean | ISiteUnderReconstruction
  ) {
    state.isUnderConstruction = val
  },
  setLayout(state: IState, val: ISettingsLayout) {
    state.layout = val || {}
  },
  setHeaderBanner(state: IState, val: IHeaderBanner | null) {
    state.headerBanner = val
  },
  setFixedHeaderHeight(state: IState, val: number | null) {
    state.fixedHeaderHeight = val
  },
  setPaySystems(state: IState, val: IPaySystem[]) {
    state.paySystems = val || []
  },
  setDadata(state: IState, val?: { token: string } | null) {
    state.dadata = val || undefined
  },
  setHost(state: IState, val: string | null) {
    state.host = val
  },
  setIsShowAppBanner(state: IState, val: boolean) {
    state.isShowAppBanner = val
  },
  setIsConfigLoad(state: IState, val: boolean) {
    state.isConfigLoad = val
  },
  setHeaderMenu(state: IState, val: ILinkItem[]) {
    state.headerMenu = val
  },
  setFooterSitemap(state: IState, val: ILayoutFooterSitemapItem[]) {
    state.footerSitemap = val
  },
  setCookieAccepted(state: IState, val: boolean) {
    state.cookieAccepted = val
  },
}

export const getters: GetterTree<IState, IStateIndex> = {
  getLocationName(state: IState): string | null {
    return state.location ? state.location.name : null
  },
  getOptionsCurrency(state: IState): string {
    return state.options ? state.options.currency : ''
  },
  getOptionsCurrencyCode(state: IState): string {
    return state.options ? state.options.currencyCode : ''
  },
  getOptionsPhoneMask(state: IState): string {
    return state.options ? state.options.phoneMask : ''
  },
  getOptionsCanChangeQuantity(state: IState): boolean {
    return state.options ? state.options.canChangeQuantity : false
  },
  getOptionsAuthType(state: IState): string {
    return state.options ? state.options.authType : 'sms'
  },
  getOptionsCatalogPageType(state: IState): string {
    return state.options ? state.options.catalogPageType : 'type-1'
  },
  getOptionsProductInStockViewType(state: IState): string {
    return state.options ? state.options.productInStockViewType : 'type-1'
  },
  getOptionsIsRunSliderImmediately(state: IState): boolean {
    return state.options ? state.options.isRunSliderImmediately : false
  },
  getOptionsProductShadow(state: IState): boolean {
    return state.options ? state.options.productShadow : false
  },
  getApiKeyYandexMap(state: IState): string | null {
    return state.apiKeys.YandexMap || null
  },
  getApiKeyRecaptchaSiteKey(state: IState): string | null {
    return state.apiKeys.recaptchaSiteKey || null
  },
  getOptionsCopyright(state: IState): boolean {
    return state.options ? state.options.copyright : true
  },
  getOptionsIsOnlyUserToAccessOrder(state: IState): boolean {
    return state.options ? state.options.isOnlyUserToAccessOrder : false
  },
  getOptionsIsShowComponentLocationQuestion(state: IState): boolean {
    return state.optionsSession
      ? state.optionsSession.isShowComponentLocationQuestion
      : false
  },
  getLayoutHeaderNumber(state: IState): 5 | 7 {
    return Number(state.layout.header) === 7 ? 7 : 5
  },
  getLayoutHeaderMobileNumber(state: IState): 2 | 4 {
    return Number(state.layout.headerMobile) === 4 ? 4 : 2
  },
  getFixedHeaderHeight(state: IState): number | null {
    return state.fixedHeaderHeight || null
  },
  getDadataToken(state: IState): string | null {
    if (state.dadata && state.dadata.token) {
      return state.dadata.token
    }
    return null
  },
}

export const actions: ActionTree<IState, IStateIndex> = {
  async configLoad({ commit, state }, { app }) {
    // читаем новый ключ, с fallback на старый (для уже выданных cookie)
    let token = app.$cookies.get('mp_token') || app.$cookies.get('token')
    if (Capacitor.isNativePlatform()) {
      const { value } = await Storage.get({ key: 'token' })
      token = value
    }
    if (token) {
      this.$axios.setToken(token)
    }

    const response = await this.$axios.$get<ISiteConfigResponse>(CONFIG_API, {
      params: {
        ip: state.ip || '',
      },
    })

    // бэк говорит «не авторизован», а $auth считает себя залогиненным —
    // это и есть баг «вылета»; делаем контролируемый logout вместо разрыва сессии
    if (response.isAuthorized === false && this.$auth.loggedIn) {
      await this.$auth.logout()
    }

    this.$axios.setToken(response.token)
    // пишем токен под новым именем, чтобы не конфликтовать с cookie модуля авторизации
    app.$cookies.set('mp_token', response.token, {
      maxAge: 60 * 60 * 24 * 365,
      domain: app.$config.cookieDomain,
      path: '/',
    })
    if (Capacitor.isNativePlatform()) {
      await Storage.set({ key: 'token', value: response.token })
    }

    commit('setToken', response.token)
    commit('setMobileApp', response.mobileApp)
    commit('setSocial', response.social)
    commit('setSocialHeader', response.socialHeader)
    commit('setFooterContacts', response.footerContacts || [])
    commit('setCompanyName', response.companyName || '')
    commit('setCopyright', response.copyright || '')
    commit('setLogo', response.logo || null)
    commit('setBrand', response.brand || '')
    commit('setOrigin', response.origin || '')
    commit('setContacts', response.contacts || {})
    commit('setPaySystems', response.paySystems || [])
    commit('setOptionsSession', response.optionsSession)
    commit('setHeaderBanner', response.headerBanner || null)
    commit('setIsUnderConstruction', response.isUnderConstruction || false)
    commit('setHeaderMenu', response.headerMenu || [])
    commit('setFooterSitemap', response.footerSitemap || [])
    commit('setIsConfigLoad', true)
    commit('setOptions', response.options)
    commit('setApiKeys', response.apiKeys)
    commit('setLayout', response.layout || {})
    commit('setCssVariables', response.cssVariables || null)
    commit('setScripts', response.scripts)
    commit('setDadata', response.dadata)
    if (state.hiddenCopyright || app.$cookies.get('hiddenCopyright')) {
      app.$cookies.set('hiddenCopyright', true, {
        maxAge: 60 * 60 * 24 * 365,
        domain: app.$config.cookieDomain,
        path: '/',
      })
      commit('setHiddenCopyright', true)
    }

    if (app.$cookies.get('cookie-consent')) {
      commit('setCookieAccepted', true)
    }
  },
  async setLocation({ commit, state }, location: ICity) {
    if (state.location && state.location.id !== location.id) {
      const response = await this.$axios.$post(LOCATION_CURRENT_API, {
        location,
      })
      commit('setLocation', response.location || null)

      if (location.url && !Capacitor.isNativePlatform()) {
        window.location.href = location.url
      } else {
        window.location.reload()
      }
    }
  },
  async getLocation({ commit }) {
    const response = await this.$axios.$get(LOCATION_CURRENT_API)
    runIdle(() => {
      commit('setLocation', response.location || null)
    })
  },

  async getCatalogMenuCategories({ commit }) {
    const response = await this.$axios.$get(CATALOG_MENU_API)
    commit('setCatalogMenuCategories', response.categories || [])
  },
}
